import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Calendar as CalendarIcon, 
  Plus, 
  Trash2, 
  MapPin, 
  Clock, 
  Users,
  Check,
  X,
  ClipboardList,
  Search,
  UserCheck,
  UserX,
  Edit,
  Flag,
  FileDown,
  Archive
} from "lucide-react";
import { format } from "date-fns";
import { sl } from "date-fns/locale";

const EventsPage = () => {
  const { user, token, hasRole } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [selectedEventForAttendance, setSelectedEventForAttendance] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [attendanceSearch, setAttendanceSearch] = useState("");
  const [rsvpListOpen, setRsvpListOpen] = useState(null); // event id or null
  const [editingEvent, setEditingEvent] = useState(null); // for editing
  const [formData, setFormData] = useState({
    naslov: "",
    opis: "",
    datum: "",
    cas: "",
    lokacija: "",
    tip: "dogodek",
    zborno_mesto: ""
  });

  useEffect(() => {
    fetchEvents();
    // Označi kategorijo dogodkov kot prebrano
    const headers = { Authorization: `Bearer ${token}` };
    axios.post(`${API}/me/notification-prefs/mark-seen`, { category: "dogodki" }, { headers }).catch(() => { /* silent */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEvents = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/events`, { headers });
      // Sort events by date ascending (upcoming first)
      const sorted = response.data.sort((a, b) => {
        const da = a.datum ? new Date(a.datum) : new Date(0);
        const db = b.datum ? new Date(b.datum) : new Date(0);
        return da - db;
      });
      setEvents(sorted);
    } catch (error) {
      toast.error("Napaka pri nalaganju dogodkov");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingEvent) {
        // Update existing event
        await axios.put(`${API}/events/${editingEvent.id}`, formData, { headers });
        toast.success("Dogodek posodobljen");
      } else {
        // Create new event
        await axios.post(`${API}/events`, formData, { headers });
        toast.success("Dogodek dodan");
      }
      
      setDialogOpen(false);
      setEditingEvent(null);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri shranjevanju");
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      naslov: event.naslov || "",
      opis: event.opis || "",
      datum: event.datum || "",
      cas: event.cas || "",
      lokacija: event.lokacija || "",
      tip: event.tip || "dogodek",
      zborno_mesto: event.zborno_mesto || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!eventToDelete) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/events/${eventToDelete.id}`, { headers });
      toast.success("Dogodek izbrisan");
      setDeleteDialogOpen(false);
      setEventToDelete(null);
      fetchEvents();
    } catch (error) {
      toast.error("Napaka pri brisanju");
    }
  };

  const openDeleteDialog = (event) => {
    setEventToDelete(event);
    setDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      naslov: "",
      opis: "",
      datum: "",
      cas: "",
      lokacija: "",
      tip: "dogodek",
      zborno_mesto: ""
    });
    setEditingEvent(null);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return format(date, "d. MMMM yyyy", { locale: sl });
  };

  const getEventTypeLabel = (tip) => {
    const labels = {
      dogodek: "Dogodek",
      sestanek: "Sestanek",
      sestanek_uo: "Sestanek UO",
      lov: "Lov",
      delovna_akcija: "Delovna akcija",
      preizkus: "Preizkus orožja"
    };
    return labels[tip] || tip;
  };

  const getEventTypeColor = (tip) => {
    const colors = {
      dogodek: "bg-blue-100 text-blue-800",
      sestanek: "bg-purple-100 text-purple-800",
      sestanek_uo: "bg-red-100 text-red-800",
      lov: "bg-green-100 text-green-800",
      delovna_akcija: "bg-orange-100 text-orange-800",
      preizkus: "bg-amber-100 text-amber-800"
    };
    return colors[tip] || "bg-gray-100 text-gray-800";
  };

  // Open attendance dialog and fetch data
  const openAttendanceDialog = async (event) => {
    setSelectedEventForAttendance(event);
    setAttendanceDialogOpen(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/events/${event.id}/attendance`, { headers });
      setAttendanceData(response.data);
    } catch (error) {
      toast.error("Napaka pri nalaganju prisotnosti");
      setAttendanceDialogOpen(false);
    }
  };

  // Update attendance for a member (clicking same status again resets to "neznan")
  const updateAttendance = async (userId, status, currentStatus) => {
    if (!selectedEventForAttendance) return;
    
    // If clicking the same status, reset to "neznan"
    const newStatus = (currentStatus === status) ? "neznan" : status;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API}/events/${selectedEventForAttendance.id}/attendance/${userId}`,
        { status: newStatus },
        { headers }
      );
      
      // Refresh attendance data
      const response = await axios.get(`${API}/events/${selectedEventForAttendance.id}/attendance`, { headers });
      setAttendanceData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri posodabljanju");
    }
  };

  // Update hunt role for a member (only for "lov" type events)
  const updateHuntRole = async (userId, vloga) => {
    if (!selectedEventForAttendance) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API}/events/${selectedEventForAttendance.id}/attendance/${userId}`,
        { vloga_lov: vloga },
        { headers }
      );
      
      // Refresh attendance data
      const response = await axios.get(`${API}/events/${selectedEventForAttendance.id}/attendance`, { headers });
      setAttendanceData(response.data);
      toast.success("Vloga posodobljena");
    } catch (error) {
      toast.error("Napaka pri posodabljanju vloge");
    }
  };

  // Hunt roles
  const huntRoles = [
    { value: "none", label: "-" },
    { value: "VL", label: "VL" },
    { value: "PVL", label: "PVL" },
    { value: "G", label: "G" },
    { value: "GP", label: "GP" },
    { value: "KUH", label: "KUH" }
  ];

  // RSVP - member marks if they're coming or not
  const handleRSVP = async (eventId, status, currentStatus) => {
    try {
      // If clicking the same status, reset (remove RSVP)
      const newStatus = (currentStatus === status) ? "neznan" : status;
      
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/events/${eventId}/attendance?status=${newStatus}`, {}, { headers });
      
      if (newStatus === "neznan") {
        toast.info("RSVP odstranjen");
      } else {
        toast.success(newStatus === "pridem" ? "Označeno: Pridem" : "Označeno: Ne pridem");
      }
      fetchEvents(); // Refresh to show updated counts
    } catch (error) {
      toast.error("Napaka pri označevanju");
    }
  };

  // Get user's RSVP status for an event
  const getUserRSVP = (event) => {
    const attendees = event.udeleženci || [];
    const myRsvp = attendees.find(a => a.user_id === user?.id);
    return myRsvp?.status || null;
  };

  // Count RSVPs for an event
  const getRSVPCounts = (event) => {
    const attendees = event.udeleženci || [];
    return {
      pridem: attendees.filter(a => a.status === "pridem").length,
      ne_pridem: attendees.filter(a => a.status === "ne_pridem").length
    };
  };

  // Get RSVP list for display
  const getRSVPList = (event) => {
    const attendees = event.udeleženci || [];
    return {
      pridem: attendees.filter(a => a.status === "pridem"),
      ne_pridem: attendees.filter(a => a.status === "ne_pridem")
    };
  };

  // Export event to PDF
  const handleExportPDF = async (eventId, eventTitle) => {
    try {
      const res = await fetch(`${API}/download/event-pdf/${eventId}?token=${token}`);
      if (!res.ok) throw new Error('Napaka');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `dogodek_${eventTitle.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      toast.success("PDF izvožen");
    } catch (error) {
      toast.error("Napaka pri izvozu PDF");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "prisoten": return "bg-green-100 text-green-800";
      case "odsoten": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "prisoten": return "Prisoten";
      case "odsoten": return "Odsoten";
      default: return "Neznan";
    }
  };

  const filteredAttendance = attendanceData?.attendance?.filter(a => {
    const fullName = `${a.ime} ${a.priimek}`.toLowerCase();
    return fullName.includes(attendanceSearch.toLowerCase());
  }) || [];

  const canManage = hasRole(["administrator", "predsednik", "gospodar"]);
  const canManageAttendance = hasRole(["administrator", "predsednik", "gospodar", "tajnik"]);
  const canExportPDF = hasRole(["administrator", "predsednik", "gospodar", "tajnik"]);
  const canArchive = hasRole(["administrator", "predsednik"]);

  const handleArchiveAttendance = async () => {
    if (!selectedEventForAttendance) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/events/${selectedEventForAttendance.id}/attendance/archive`, {}, { headers });
      toast.success("Prisotnost arhivirana");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("Prisotnost za ta dogodek je že arhivirana");
      } else {
        toast.error("Napaka pri arhiviranju");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="events-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary flex items-center gap-3">
            <CalendarIcon className="h-7 w-7 sm:h-8 sm:w-8" />
            Dogodki
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {events.length} dogodkov
          </p>
        </div>
        
        {canManage && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-event-btn" className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Dodaj dogodek
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md mx-4">
              <DialogHeader>
                <DialogTitle>{editingEvent ? "Uredi dogodek" : "Nov dogodek"}</DialogTitle>
                <DialogDescription>
                  {editingEvent ? "Uredite podatke dogodka" : "Dodajte nov dogodek ali delovno akcijo"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="naslov">Naslov</Label>
                  <Input
                    id="naslov"
                    value={formData.naslov}
                    onChange={(e) => setFormData({ ...formData, naslov: e.target.value })}
                    placeholder="Ime dogodka"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tip">Tip dogodka</Label>
                  <Select 
                    onValueChange={(value) => setFormData({ ...formData, tip: value })} 
                    value={formData.tip}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Izberite tip" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dogodek">Dogodek</SelectItem>
                      <SelectItem value="sestanek">Sestanek</SelectItem>
                      <SelectItem value="sestanek_uo">Sestanek UO</SelectItem>
                      <SelectItem value="lov">Lov</SelectItem>
                      <SelectItem value="delovna_akcija">Delovna akcija</SelectItem>
                      <SelectItem value="preizkus">Preizkus orožja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="datum">Datum</Label>
                    <Input
                      id="datum"
                      type="date"
                      value={formData.datum}
                      onChange={(e) => setFormData({ ...formData, datum: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cas">Ura</Label>
                    <Input
                      id="cas"
                      type="time"
                      value={formData.cas}
                      onChange={(e) => setFormData({ ...formData, cas: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lokacija">Lokacija</Label>
                  <Input
                    id="lokacija"
                    value={formData.lokacija}
                    onChange={(e) => setFormData({ ...formData, lokacija: e.target.value })}
                    placeholder="Kraj dogodka"
                  />
                </div>

                {/* Zborno mesto - only for "lov" type */}
                {formData.tip === "lov" && (
                  <div className="space-y-2">
                    <Label htmlFor="zborno_mesto">Zborno mesto</Label>
                    <Input
                      id="zborno_mesto"
                      value={formData.zborno_mesto}
                      onChange={(e) => setFormData({ ...formData, zborno_mesto: e.target.value })}
                      placeholder="Kraj zbiranja za lov"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="opis">Opis</Label>
                  <Textarea
                    id="opis"
                    value={formData.opis}
                    onChange={(e) => setFormData({ ...formData, opis: e.target.value })}
                    placeholder="Opis dogodka"
                    rows={2}
                  />
                </div>

                <DialogFooter className="gap-2">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Prekliči
                  </Button>
                  <Button type="submit">Dodaj</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Events List */}
      {events.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <CalendarIcon className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">Ni načrtovanih dogodkov</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {events.map((event) => {
            const myRsvp = getUserRSVP(event);
            const rsvpCounts = getRSVPCounts(event);
            
            return (
            <Card 
              key={event.id} 
              className={`${event.tip === 'delovna_akcija' ? 'border-l-4 border-l-orange-500' : ''}`}
              data-testid={`event-card-${event.id}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col gap-3">
                  {/* Event info */}
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={getEventTypeColor(event.tip)}>
                          {getEventTypeLabel(event.tip)}
                        </Badge>
                        {/* Combined attendance count (RSVP "pridem" + admin "prisoten" merged) */}
                        {((event.prisotni_count || 0) > 0 || (event.odsotni_count || 0) > 0) && (
                          <button 
                            onClick={() => setRsvpListOpen(rsvpListOpen === event.id ? null : event.id)}
                            className="flex items-center gap-2 text-xs hover:bg-muted px-2 py-1 rounded-md transition-colors"
                            data-testid={`rsvp-toggle-${event.id}`}
                            title="Prisotnost (RSVP + admin)"
                          >
                            <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Prisotnost:</span>
                            {event.prisotni_count > 0 && (
                              <span className="flex items-center gap-1 text-green-600">
                                <UserCheck className="h-3 w-3" />
                                {event.prisotni_count}
                              </span>
                            )}
                            {event.odsotni_count > 0 && (
                              <span className="flex items-center gap-1 text-red-600">
                                <UserX className="h-3 w-3" />
                                {event.odsotni_count}
                              </span>
                            )}
                          </button>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold">{event.naslov}</h3>
                      {event.opis && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{event.opis}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-xs sm:text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="h-3 w-3" />
                          {formatDate(event.datum)}
                        </span>
                        {event.cas && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {event.cas}
                          </span>
                        )}
                        {event.lokacija && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {event.lokacija}
                          </span>
                        )}
                        {event.zborno_mesto && event.tip === "lov" && (
                          <span className="flex items-center gap-1 text-green-700">
                            <Flag className="h-3 w-3" />
                            Zbor: {event.zborno_mesto}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Manager actions */}
                    {(canManage || canManageAttendance) && (
                      <div className="flex items-center gap-2">
                        {canManageAttendance && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openAttendanceDialog(event)}
                            data-testid={`attendance-btn-${event.id}`}
                          >
                            <ClipboardList className="h-4 w-4 mr-1" />
                            Prisotnost
                          </Button>
                        )}
                        {canManage && (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-primary hover:bg-primary/10"
                              onClick={() => handleEdit(event)}
                              data-testid={`edit-event-${event.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-destructive hover:bg-destructive/10"
                              onClick={() => openDeleteDialog(event)}
                              data-testid={`delete-event-${event.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* RSVP buttons for all members */}
                  <div className="flex items-center justify-between gap-2 pt-2 border-t">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground mr-2">Vaša prijava:</span>
                      <Button
                        size="sm"
                        variant={myRsvp === "pridem" ? "default" : "outline"}
                        className={myRsvp === "pridem" ? "bg-green-600 hover:bg-green-700" : "hover:bg-green-50 hover:text-green-700 hover:border-green-300"}
                        onClick={() => handleRSVP(event.id, "pridem", myRsvp)}
                        data-testid={`rsvp-pridem-${event.id}`}
                      >
                        <UserCheck className="h-4 w-4 mr-1" />
                        Pridem
                      </Button>
                      <Button
                        size="sm"
                        variant={myRsvp === "ne_pridem" ? "default" : "outline"}
                        className={myRsvp === "ne_pridem" ? "bg-red-600 hover:bg-red-700" : "hover:bg-red-50 hover:text-red-700 hover:border-red-300"}
                        onClick={() => handleRSVP(event.id, "ne_pridem", myRsvp)}
                        data-testid={`rsvp-ne-pridem-${event.id}`}
                    >
                      <UserX className="h-4 w-4 mr-1" />
                      Ne pridem
                      </Button>
                    </div>
                    
                    {/* Export PDF button - only for predsednik, gospodar, tajnik */}
                    {canExportPDF && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleExportPDF(event.id, event.naslov)}
                        data-testid={`export-pdf-${event.id}`}
                        className="text-muted-foreground hover:text-primary"
                      >
                        <FileDown className="h-4 w-4 mr-1" />
                        PDF
                      </Button>
                    )}
                  </div>

                  {/* RSVP List - expandable */}
                  {rsvpListOpen === event.id && (
                    <div className="border-t pt-3 mt-1 animate-fade-in" data-testid={`rsvp-list-${event.id}`}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Pridem list */}
                        <div>
                          <h4 className="text-sm font-medium text-green-700 flex items-center gap-1 mb-2">
                            <UserCheck className="h-4 w-4" />
                            Pridem ({getRSVPList(event).pridem.length})
                          </h4>
                          {getRSVPList(event).pridem.length === 0 ? (
                            <p className="text-xs text-muted-foreground">Nihče še ni označil</p>
                          ) : (
                            <ul className="space-y-1">
                              {getRSVPList(event).pridem.map((a) => (
                                <li key={a.user_id || `${a.ime}-${a.priimek}`} className="text-sm flex items-center gap-2">
                                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                  {a.ime} {a.priimek}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        {/* Ne pridem list */}
                        <div>
                          <h4 className="text-sm font-medium text-red-700 flex items-center gap-1 mb-2">
                            <UserX className="h-4 w-4" />
                            Ne pridem ({getRSVPList(event).ne_pridem.length})
                          </h4>
                          {getRSVPList(event).ne_pridem.length === 0 ? (
                            <p className="text-xs text-muted-foreground">Nihče še ni označil</p>
                          ) : (
                            <ul className="space-y-1">
                              {getRSVPList(event).ne_pridem.map((a) => (
                                <li key={a.user_id || `${a.ime}-${a.priimek}`} className="text-sm flex items-center gap-2">
                                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                  {a.ime} {a.priimek}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )})}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="max-w-sm mx-4">
          <AlertDialogHeader>
            <AlertDialogTitle>Izbriši dogodek?</AlertDialogTitle>
            <AlertDialogDescription>
              Ali ste prepričani, da želite izbrisati "{eventToDelete?.naslov}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel onClick={() => setEventToDelete(null)}>Prekliči</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Izbriši
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Attendance Dialog */}
      <Dialog open={attendanceDialogOpen} onOpenChange={(open) => { 
        setAttendanceDialogOpen(open); 
        if (!open) {
          setSelectedEventForAttendance(null);
          setAttendanceData(null);
          setAttendanceSearch("");
        }
      }}>
        <DialogContent className="max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Prisotnost
            </DialogTitle>
            <DialogDescription>
              {selectedEventForAttendance?.naslov} • {selectedEventForAttendance && formatDate(selectedEventForAttendance.datum)}
            </DialogDescription>
          </DialogHeader>

          {attendanceData ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 rounded-lg bg-green-50">
                  <p className="text-xl font-bold text-green-600">{attendanceData.summary.prisotni}</p>
                  <p className="text-xs text-green-700">Prisotni</p>
                </div>
                <div className="p-2 rounded-lg bg-red-50">
                  <p className="text-xl font-bold text-red-600">{attendanceData.summary.odsotni}</p>
                  <p className="text-xs text-red-700">Odsotni</p>
                </div>
                <div className="p-2 rounded-lg bg-gray-50">
                  <p className="text-xl font-bold text-gray-600">{attendanceData.summary.neznani}</p>
                  <p className="text-xs text-gray-700">Neznano</p>
                </div>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Išči člana..."
                  value={attendanceSearch}
                  onChange={(e) => setAttendanceSearch(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Members List (navaden div namesto ScrollArea — Radix ScrollArea na iOS blokira tape) */}
              <div className="h-[300px] overflow-y-auto border rounded-lg" style={{ WebkitOverflowScrolling: "touch" }}>
                <div className="p-2 space-y-1">
                  {filteredAttendance.map((member) => (
                    <div
                      key={member.user_id}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 gap-2"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {member.ime} {member.priimek}
                        </p>
                        {/* Show hunt role badge if set */}
                        {member.vloga_lov && (
                          <Badge variant="outline" className="text-xs mt-0.5 bg-amber-50 text-amber-700 border-amber-300">
                            {member.vloga_lov}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Hunt role selector - only for "lov" type events */}
                      {selectedEventForAttendance?.tip === "lov" && (
                        <Select
                          value={member.vloga_lov || "none"}
                          onValueChange={(value) => updateHuntRole(member.user_id, value === "none" ? "" : value)}
                        >
                          <SelectTrigger className="w-[70px] h-7 text-xs">
                            <SelectValue placeholder="-" />
                          </SelectTrigger>
                          <SelectContent>
                            {huntRoles.map((role) => (
                              <SelectItem key={role.value} value={role.value} className="text-xs">
                                {role.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          size="icon"
                          variant={member.status === "prisoten" ? "default" : "outline"}
                          className={`h-8 w-8 touch-manipulation ${member.status === "prisoten" ? "bg-green-600 hover:bg-green-700" : ""}`}
                          onClick={() => updateAttendance(member.user_id, "prisoten", member.status)}
                          title="Prisoten"
                          data-testid={`attendance-prisoten-${member.user_id}`}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant={member.status === "odsoten" ? "default" : "outline"}
                          className={`h-8 w-8 touch-manipulation ${member.status === "odsoten" ? "bg-red-600 hover:bg-red-700" : ""}`}
                          onClick={() => updateAttendance(member.user_id, "odsoten", member.status)}
                          title="Odsoten"
                          data-testid={`attendance-odsoten-${member.user_id}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-32">
              <div className="spinner" />
            </div>
          )}

          <DialogFooter className="flex gap-2">
            {canArchive && attendanceData && (
              <Button variant="outline" onClick={handleArchiveAttendance} className="mr-auto" data-testid="archive-attendance-btn">
                <Archive className="h-4 w-4 mr-2" />
                Arhiviraj
              </Button>
            )}
            <Button variant="outline" onClick={() => setAttendanceDialogOpen(false)}>
              Zapri
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventsPage;
